if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        // Product-page arrows select one media item, rather than paging thumbnails.
        this.mediaNavigation = Boolean(this.closest('.keast-product-refresh'));
        if (this.mediaNavigation) {
          // The gallery owns arrow boundaries; the strip still supports touch scrolling.
          this.elements.thumbnails.enableSliderLooping = true;
          this.elements.thumbnails.querySelectorAll('.thumbnail__badge').forEach((badge) => {
            badge.style.pointerEvents = 'none';
          });
          this.elements.thumbnails.querySelectorAll('button[name="previous"], button[name="next"]').forEach((button) => {
            button.classList.remove('small-hide', 'medium-hide', 'large-up-hide');
            button.setAttribute('aria-controls', this.elements.viewer.id);
          });
          this.elements.thumbnails.addEventListener('click', (event) => {
            const button = event.target.closest('button[name="previous"], button[name="next"]');
            if (!button || button.closest('slider-component') !== this.elements.thumbnails) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            this.changeMedia(button.name === 'next' ? 1 : -1);
          }, true);
          this.syncMediaNavigation();
          new ResizeObserver(() => this.syncMediaNavigation()).observe(this.elements.thumbnails);
        }
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      getNavigableThumbnails() {
        return Array.from(this.elements.thumbnails.querySelectorAll('[data-target]')).filter(
          (thumbnail) => thumbnail.clientWidth > 0
        );
      }

      changeMedia(direction) {
        const thumbnails = this.getNavigableThumbnails();
        const index = thumbnails.findIndex((thumbnail) => thumbnail.querySelector('[aria-current="true"]'));
        const target = thumbnails[index + direction];
        if (target) this.setActiveMedia(target.dataset.target, false);
      }

      syncMediaNavigation() {
        if (!this.mediaNavigation) return;
        const thumbnails = this.getNavigableThumbnails();
        const index = thumbnails.findIndex((thumbnail) => thumbnail.querySelector('[aria-current="true"]'));
        this.elements.thumbnails.querySelector('button[name="previous"]').disabled = index <= 0;
        this.elements.thumbnails.querySelector('button[name="next"]').disabled = index < 0 || index >= thumbnails.length - 1;
      }

      onSlideChanged(event) {
        const currentElement = this.mediaNavigation
          ? this.elements.viewer.sliderItemsToShow?.[this.elements.viewer.currentPage - 1]
          : event.detail.currentElement;
        if (!currentElement) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
              behavior: this.mediaNavigation ? 'instant' : 'auto',
            });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        this.syncMediaNavigation();
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
